import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import Can from "@/components/usm/Can";
import { toast } from "sonner";
import {
  Printer, Ban, ArrowLeft, Edit3, CalendarClock,
  Share2, MessageCircle, FileDown, X, Loader2, ShieldCheck, Mail,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });

// Phase 54.12 — Warranty period lookup shared between Receipt (display),
// the "Garantiyi Düzenle" dialog (dropdown options), and the audit /
// backend contract (`warranty_months` scalar).
//   months > 0 → normal fixed-length warranty
//   months == -1 → Lifetime (Ömür Boyu)
//   months == 0  → No warranty (Garantisiz)
const WARRANTY_OPTIONS = [
  { months: 6,   label_tr: "6 Ay",       label_en: "6 Months" },
  { months: 12,  label_tr: "1 Yıl",      label_en: "1 Year", isDefault: true },
  { months: 18,  label_tr: "18 Ay",      label_en: "18 Months" },
  { months: 24,  label_tr: "2 Yıl",      label_en: "2 Years" },
  { months: 60,  label_tr: "5 Yıl",      label_en: "5 Years" },
  { months: -1,  label_tr: "Ömür Boyu",  label_en: "Lifetime" },
  { months: 0,   label_tr: "Garantisiz", label_en: "No Warranty" },
];

function warrantyLabel(months) {
  const opt = WARRANTY_OPTIONS.find((o) => o.months === months);
  if (opt) return `${opt.label_tr} / ${opt.label_en}`;
  // Legacy sales without the field default to 1 year — matches the
  // default in the backend and the Phase 54.12 spec.
  const fallback = WARRANTY_OPTIONS.find((o) => o.isDefault);
  return `${fallback.label_tr} / ${fallback.label_en}`;
}

function computeExpiry(startIso, months) {
  if (months == null || months === -1 || months === 0) return null;
  const start = new Date(startIso);
  if (isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setMonth(end.getMonth() + months);
  return end;
}

function fmtWarrantyDate(d) {
  if (!d) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

const PAY_LABEL = {
  cash:     "Nakit",
  card:     "Kredi Kartı",
  transfer: "Havale/EFT",
  other:    "Diğer",
};

export default function SaleDetail() {
  const { sid } = useParams();
  const [params] = useSearchParams();
  const autoprint = params.get("print") === "1";
  const { hasPerm, employee: currentEmp } = useUsmAuth();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voiding, setVoiding] = useState(false);
  const [site, setSite] = useState(null);
  const [correcting, setCorrecting] = useState(false);
  const [correctingMeta, setCorrectingMeta] = useState(false);
  const [correctingWarranty, setCorrectingWarranty] = useState(false);
  // Phase 54.11 — Sharing state
  const [sharingPdf, setSharingPdf] = useState(false);
  const [waTarget, setWaTarget] = useState(null); // triggers WhatsApp dialog
  const [emailTarget, setEmailTarget] = useState(null); // triggers Email dialog

  // Phase 54.7.C — same admin/cemil gate as the backend endpoint.
  const canCorrect = useMemo(() => {
    const u = (currentEmp?.username || "").toLowerCase();
    return u === "admin" || u === "cemil";
  }, [currentEmp?.username]);

  // Receipt header / legal info comes from /api/settings/site so the
  // address, tax info, MERSİS etc. can be edited by admin without redeploys.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const r = await fetch(`${baseUrl}/api/settings/site`);
        if (r.ok && mounted) setSite(await r.json());
      } catch { /* receipt still renders with defaults */ }
    })();
    return () => { mounted = false; };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await usmApi.get(`/sales/${sid}`);
      setSale(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Satış yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid]);
  // Auto-print on first arrival from POS submit
  useEffect(() => {
    if (autoprint && sale && !loading) {
      setTimeout(() => window.print(), 350);
    }
    // eslint-disable-next-line
  }, [sale, loading]);

  const voidSale = async () => {
    const reason = window.prompt("İptal nedenini yazın:");
    if (reason === null) return;
    setVoiding(true);
    try {
      await usmApi.post(`/sales/${sid}/void`, { reason });
      toast.success("Satış iptal edildi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İptal başarısız"); }
    finally { setVoiding(false); }
  };

  // ── Phase 54.11 — Receipt sharing helpers ─────────────────────────
  //
  // The PDF is generated CLIENT-SIDE from the exact same DOM node
  // (`[data-testid="sale-receipt"]`) that `window.print()` uses. That
  // guarantees the printed receipt and shared PDF are byte-identical —
  // no risk of the two diverging as the receipt template evolves.
  //
  // html2pdf.js internally uses html2canvas + jsPDF; the config below
  // matches the print @page margin (12 mm) so screen, print and shared
  // PDF all look the same.

  // Filename convention: <SALE_NO>_<DD-MM-YYYY>_UzungolKuyumculuk.pdf
  // — uses the SALE DATE (not "now") so a receipt shared weeks later
  // still shows the transaction date in the filename.
  const receiptFilename = useMemo(() => {
    const saleDate = sale?.created_at ? new Date(sale.created_at) : new Date();
    const dd = String(saleDate.getDate()).padStart(2, "0");
    const mm = String(saleDate.getMonth() + 1).padStart(2, "0");
    const yyyy = saleDate.getFullYear();
    return `${sale?.sale_no || "SATIS"}_${dd}-${mm}-${yyyy}_UzungolKuyumculuk.pdf`;
  }, [sale?.sale_no, sale?.created_at]);

  const generatePdfBlob = async () => {
    const node = document.querySelector('[data-testid="sale-receipt"]');
    if (!node) throw new Error("Fiş DOM'da bulunamadı");
    // Lazy-load html2pdf so the initial bundle stays lean.
    const { default: html2pdf } = await import("html2pdf.js");
    const opt = {
      margin: [12, 12, 12, 12], // mm — matches @page in Receipt CSS
      filename: receiptFilename,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    };
    return await html2pdf().set(opt).from(node).outputPdf("blob");
  };

  const auditShare = async (channel, extra = {}) => {
    // Fire-and-forget audit log — a failure here should NOT block the
    // user's share action, so we swallow the error but toast a warning
    // in case the operator wants to retry manually later.
    try {
      await usmApi.post(`/sales/${sid}/share-audit`, { channel, ...extra });
    } catch (e) {
      console.warn("[share-audit] failed:", e?.response?.data || e);
    }
  };

  const downloadPdf = async () => {
    setSharingPdf(true);
    try {
      const blob = await generatePdfBlob();
      const filename = receiptFilename;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      window.setTimeout(() => {
        try { a.remove(); } catch (_) { /* detached */ }
        try { URL.revokeObjectURL(url); } catch (_) { /* released */ }
      }, 500);
      toast.success(`${filename} indirildi`);
      auditShare("pdf", { filename });
    } catch (e) {
      console.error("[pdf-download] failed:", e);
      toast.error("PDF oluşturulamadı: " + (e?.message || e));
    } finally {
      setSharingPdf(false);
    }
  };

  const openWhatsAppShare = () => {
    // Prefill customer phone if we have one. The dialog will always
    // download the PDF first (so the operator has the file at hand)
    // and then open wa.me — Meta doesn't allow attaching files via
    // the URL scheme, so the operator manually attaches after the
    // chat opens. Standard pattern for every jewelry store using
    // WhatsApp for receipts.
    setWaTarget({ phone: sale?.customer_phone || "" });
  };

  const openEmailShare = () => {
    // Prefill customer email if it exists on the sale row (currently
    // not part of the create-sale form but future-proof for when the
    // POS captures it). The dialog otherwise starts blank.
    setEmailTarget({ email: sale?.customer_email || "" });
  };

  if (loading) return <div className="p-6 text-[#71717A]">Yükleniyor...</div>;
  if (!sale)   return <div className="p-6 text-red-600">Satış bulunamadı</div>;

  return (
    <div data-testid="usm-sale-detail">
      <div className="print:hidden">
        <PageHeader
          title={`Satış ${sale.sale_no}`}
          breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Satışlar", to: "/usm/sales" }, { label: sale.sale_no }]}
          actions={
            <div className="flex items-center gap-2">
              <Link to="/usm/sales" className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Geri
              </Link>
              <button data-testid="sale-print-btn" onClick={() => window.print()}
                className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
                <Printer size={14} /> Fişi Yazdır
              </button>
              {sale.status === "completed" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="sale-share-btn"
                      disabled={sharingPdf}
                      className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2 disabled:opacity-40"
                    >
                      {sharingPdf
                        ? <Loader2 size={14} className="animate-spin" />
                        : <Share2 size={14} />}
                      Paylaş
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[220px]">
                    <DropdownMenuItem
                      data-testid="share-whatsapp"
                      disabled={sharingPdf}
                      onClick={openWhatsAppShare}
                      className="cursor-pointer text-[12px] tracking-wide"
                    >
                      <MessageCircle size={14} className="mr-2 text-emerald-600" />
                      WhatsApp ile Gönder
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="share-email"
                      disabled={sharingPdf}
                      onClick={openEmailShare}
                      className="cursor-pointer text-[12px] tracking-wide"
                    >
                      <Mail size={14} className="mr-2 text-[#C8A96A]" />
                      E-posta Gönder
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="share-pdf-download"
                      disabled={sharingPdf}
                      onClick={downloadPdf}
                      className="cursor-pointer text-[12px] tracking-wide"
                    >
                      <FileDown size={14} className="mr-2 text-[#0A0A0A]" />
                      PDF İndir
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {sale.status === "completed" && canCorrect && (
                <button
                  data-testid="sale-correct-payment-btn"
                  onClick={() => setCorrecting(true)}
                  className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-2"
                >
                  <Edit3 size={14} /> Ödemeyi Düzelt
                </button>
              )}
              {sale.status === "completed" && canCorrect && (
                <button
                  data-testid="sale-correct-metadata-btn"
                  onClick={() => setCorrectingMeta(true)}
                  className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-2"
                >
                  <CalendarClock size={14} /> Tarih/Şube Düzelt
                </button>
              )}
              {sale.status === "completed" && canCorrect && (
                <button
                  data-testid="sale-correct-warranty-btn"
                  onClick={() => setCorrectingWarranty(true)}
                  className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-2"
                >
                  <ShieldCheck size={14} /> Garantiyi Düzenle
                </button>
              )}
              {sale.status === "completed" && hasPerm("sales.manage") && (
                <Can perm="sales.manage">
                  <button data-testid="sale-void-btn" onClick={voidSale} disabled={voiding}
                    className="text-xs uppercase tracking-[0.2em] px-4 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-2 disabled:opacity-40">
                    <Ban size={14} /> Satışı İptal Et
                  </button>
                </Can>
              )}
            </div>
          }
        />
      </div>

      <Receipt sale={sale} site={site} />
      {correcting && (
        <PaymentCorrectionDialog
          sale={sale}
          onClose={() => setCorrecting(false)}
          onDone={() => { setCorrecting(false); load(); }}
        />
      )}
      {correctingMeta && (
        <MetadataCorrectionDialog
          sale={sale}
          onClose={() => setCorrectingMeta(false)}
          onDone={() => { setCorrectingMeta(false); load(); }}
        />
      )}
      {waTarget && (
        <WhatsAppShareDialog
          sale={sale}
          initialPhone={waTarget.phone}
          filename={receiptFilename}
          onClose={() => setWaTarget(null)}
          generatePdfBlob={generatePdfBlob}
          onAudited={(payload) => auditShare("whatsapp", payload)}
        />
      )}
      {emailTarget && (
        <EmailShareDialog
          sale={sale}
          initialEmail={emailTarget.email}
          filename={receiptFilename}
          onClose={() => setEmailTarget(null)}
          generatePdfBlob={generatePdfBlob}
        />
      )}
      {correctingWarranty && (
        <WarrantyCorrectionDialog
          sale={sale}
          onClose={() => setCorrectingWarranty(false)}
          onDone={() => { setCorrectingWarranty(false); load(); }}
        />
      )}
    </div>
  );
}

/**
 * Print-friendly receipt — single column, mm-aware widths via @page CSS
 * so it renders cleanly both on screen and on 80mm thermal printers / A4.
 */
function Receipt({ sale, site }) {
  const subtotal = Number(sale.subtotal || 0);
  const discount = Number(sale.discount_amount || 0);
  const tax = Number(sale.tax_amount || 0);
  const grand = Number(sale.grand_total || 0);
  const s = site || {};
  const brand = (s.site_name || "Uzungöl Kuyumculuk").toUpperCase();
  const addrLine = [s.address_line1, s.address_line2].filter(Boolean).join(" · ");
  const contactBits = [s.phone, s.website_url, s.email].filter(Boolean).join(" · ");
  const legalBits = [
    s.tax_office && `V.D. ${s.tax_office}`,
    s.tax_number && `VKN ${s.tax_number}`,
    s.mersis_number && `MERSİS ${s.mersis_number}`,
  ].filter(Boolean).join(" · ");
  const thankYou = s.thank_you_message || "Bizi tercih ettiğiniz için teşekkür ederiz.";
  return (
    <div data-testid="sale-receipt" className="bg-white border border-[#E7E5E4] print:border-0 max-w-[640px] mx-auto p-6 print:p-0">
      {/* Header */}
      <div className="text-center mb-5">
        <div data-testid="receipt-brand" className="font-heading text-2xl tracking-wider">{brand}</div>
        {addrLine && <div data-testid="receipt-address" className="text-[10.5px] text-[#71717A] mt-1 leading-snug">{addrLine}</div>}
        {contactBits && <div data-testid="receipt-contact" className="text-[10.5px] text-[#71717A] mt-0.5 leading-snug">{contactBits}</div>}
        {legalBits && <div data-testid="receipt-legal" className="text-[10px] text-[#A1A1AA] mt-0.5 font-mono leading-snug">{legalBits}</div>}
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#71717A] mt-2.5">SATIŞ FİŞİ</div>
        <div
          data-testid="receipt-warranty-title"
          className="text-[11px] uppercase tracking-[0.25em] text-[#C8A96A] mt-0.5"
        >
          GARANTİ BELGESİ <span className="text-[#A8A29E] tracking-[0.15em]">/ Warranty Certificate</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-[12px] mb-5 pb-4 border-b border-dashed border-[#E7E5E4]">
        <Info label="Satış No"  value={sale.sale_no} mono />
        <Info label="Tarih"      value={new Date(sale.created_at).toLocaleString("tr-TR")} />
        <Info label="Şube"       value={sale.branch?.name || "—"} />
        <Info label="Çalışan"    value={sale.employee?.full_name || sale.employee?.username || "—"} />
        {sale.customer_name && <Info label="Müşteri" value={sale.customer_name} />}
        {sale.customer_phone && <Info label="Telefon" value={sale.customer_phone} />}
        {sale.status !== "completed" && <Info label="Durum" value={sale.status === "voided" ? "İPTAL" : sale.status} highlight />}
      </div>

      {/* Items */}
      <table className="w-full text-[12px] mb-4">
        <thead className="border-b border-[#E7E5E4]">
          <tr className="text-[10px] uppercase tracking-[0.15em] text-[#71717A]">
            <th className="text-left py-1.5">Stok Kodu</th>
            <th className="text-left py-1.5">Ürün</th>
            <th className="text-right py-1.5">Adet</th>
            <th className="text-right py-1.5">Birim</th>
            <th className="text-right py-1.5">Tutar</th>
          </tr>
        </thead>
        <tbody>
          {(sale.items || []).map((it) => (
            <tr key={it.id} className="border-b border-dashed border-[#F5F5F4]">
              <td className="py-1.5 font-mono">{it.stock_code}</td>
              <td className="py-1.5">
                {it.product_name}
                {(it.material || it.weight_grams != null) && (
                  <div className="text-[10px] text-[#71717A]">{it.material}{it.weight_grams != null && ` · ${it.weight_grams}g`}</div>
                )}
              </td>
              <td className="py-1.5 text-right">{it.qty}</td>
              <td className="py-1.5 text-right font-finance">{TRY.format(it.unit_price)}</td>
              <td className="py-1.5 text-right font-finance">{TRY.format(it.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="ml-auto max-w-[280px] space-y-1 text-[13px] mb-5">
        <TotalsRow label="Ara Toplam" value={TRY.format(subtotal)} />
        {discount > 0 && <TotalsRow label="İndirim" value={`-${TRY.format(discount)}`} />}
        {tax > 0 && <TotalsRow label="Vergi" value={TRY.format(tax)} />}
        <div className="border-t-2 border-[#0A0A0A] pt-1.5 flex items-center justify-between font-medium">
          <span className="uppercase tracking-[0.15em] text-[11px]">Genel Toplam</span>
          <span data-testid="receipt-grand-total" className="font-finance text-lg">{TRY.format(grand)}</span>
        </div>
      </div>

      {/* Payments */}
      <div className="mb-5 border-t border-dashed border-[#E7E5E4] pt-3">
        <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Ödemeler</h3>
        <div className="space-y-1">
          {(sale.payments || []).map((p) => (
            <div key={p.id} className="flex items-center justify-between text-[12px]" data-testid={`receipt-payment-${p.payment_type}`}>
              <span>{PAY_LABEL[p.payment_type] || p.payment_type}{p.reference_no ? ` · ${p.reference_no}` : ""}</span>
              <span className="font-finance">{TRY.format(p.amount)}</span>
            </div>
          ))}
        </div>
      </div>

      {sale.note && (
        <div className="mb-5 text-[11px] text-[#71717A] italic">Not: {sale.note}</div>
      )}

      {/* Phase 54.12 — Warranty Certificate section. The Sales Receipt
          also serves as the official warranty document. Values shown
          are bilingual (Turkish / English) so the same physical
          document works for local and international customers. */}
      <WarrantySection sale={sale} />

      <div data-testid="receipt-thank-you" className="text-center text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] border-t border-dashed border-[#E7E5E4] pt-3">
        {thankYou}
      </div>

      {/* Print rules: hide app shell, ensure receipt fills page cleanly */}
      <style>{`
        @media print {
          body, html { background: white !important; }
          @page { margin: 12mm; }
          /* Hide everything except the receipt */
          aside, header, nav, [data-usm-shell-hide-on-print] { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function Info({ label, value, mono, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</div>
      <div className={(mono ? "font-mono " : "") + (highlight ? "text-red-600 font-medium" : "")}>{value}</div>
    </div>
  );
}

function TotalsRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[#52525B]">
      <span className="text-[11px] uppercase tracking-[0.15em]">{label}</span>
      <span className="font-finance">{value}</span>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * Phase 54.7.C — Payment Correction Dialog
 *
 * Lets admin/cemil edit the payment breakdown of a completed sale. The
 * dialog enforces the same rules as the backend:
 *   • ≥1 payment row
 *   • all amounts > 0
 *   • sum(amount) === sale.grand_total (1 kuruş tolerance)
 *   • reason ≥ 5 characters, ≤ 500
 * Backend runs the same validation authoritatively — we just prevent
 * obviously-broken submits from hitting the server.
 * ═══════════════════════════════════════════════════════════════════════ */
function PaymentCorrectionDialog({ sale, onClose, onDone }) {
  const grand = Number(sale.grand_total || 0);
  const initial = (sale.payments || []).length > 0
    ? sale.payments.map((p) => ({
        payment_type: p.payment_type,
        amount: Number(p.amount || 0),
        reference_no: p.reference_no || "",
      }))
    : [{ payment_type: "cash", amount: grand, reference_no: "" }];
  const [rows, setRows] = useState(initial);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await usmApi.get(`/sales/${sale.id}/corrections`);
        setHistory(r.data || []);
      } catch { /* not fatal */ }
    })();
  }, [sale.id]);

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const balanced = Math.abs(total - grand) <= 0.01;
  const anyInvalid = rows.some((r) => !r.payment_type || !(Number(r.amount) > 0));
  const canSubmit = balanced && !anyInvalid && reason.trim().length >= 5 && !submitting;

  const setRow = (i, patch) => setRows((prev) => prev.map((r, ix) => ix === i ? { ...r, ...patch } : r));
  const addRow = () => setRows((prev) => [...prev, { payment_type: "cash", amount: 0, reference_no: "" }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, ix) => ix !== i));

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        reason: reason.trim(),
        payments: rows.map((r) => ({
          payment_type: r.payment_type,
          amount: Number(r.amount),
          reference_no: r.reference_no?.trim() || null,
        })),
      };
      await usmApi.patch(`/sales/${sale.id}/payments`, body);
      toast.success("Ödeme başarıyla düzeltildi. Muhasebe otomatik güncellendi.");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(", ") : (d || "Düzeltme başarısız");
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 print:hidden">
      <div className="bg-white border border-[#E7E5E4] w-full max-w-2xl mx-4" data-testid="payment-correction-dialog">
        <div className="border-b border-[#E7E5E4] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#71717A]">Ödemeyi Düzelt</div>
            <div className="text-lg font-serif mt-1">
              {sale.sale_no}
              <span className="text-[#71717A] text-sm ml-2 font-normal">{TRY.format(grand)}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-[#71717A] hover:text-[#000] text-2xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border-l-2 border-amber-400 px-3 py-2 text-[13px] text-[#78350F]">
            Tamamlanmış bir satışın ödeme yöntemini düzeltiyorsunuz. Muhasebe, kasa/POS/banka hesapları
            ve raporlar otomatik yeniden hesaplanır. Envanter, barkod, etiket ve stok hareketleri etkilenmez.
            İşlem geri alınamaz — ancak tekrar düzeltme yapılabilir.
          </div>

          {/* payment rows */}
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`payment-row-${i}`}>
                <select
                  data-testid={`payment-type-${i}`}
                  value={r.payment_type}
                  onChange={(e) => setRow(i, { payment_type: e.target.value })}
                  className="px-3 py-2 border border-[#E7E5E4] text-sm flex-1"
                >
                  <option value="cash">Nakit</option>
                  <option value="card">POS (Kredi Kartı)</option>
                  <option value="transfer">Havale/EFT</option>
                </select>
                <input
                  type="number"
                  data-testid={`payment-amount-${i}`}
                  value={r.amount}
                  step="0.01"
                  min="0"
                  onChange={(e) => setRow(i, { amount: e.target.value })}
                  className="w-40 px-3 py-2 border border-[#E7E5E4] text-sm text-right font-finance"
                />
                <input
                  type="text"
                  data-testid={`payment-ref-${i}`}
                  value={r.reference_no}
                  placeholder="Ref. no (opsiyonel)"
                  onChange={(e) => setRow(i, { reference_no: e.target.value })}
                  className="w-36 px-3 py-2 border border-[#E7E5E4] text-sm"
                />
                {rows.length > 1 && (
                  <button
                    data-testid={`payment-remove-${i}`}
                    onClick={() => removeRow(i)}
                    className="px-2 py-2 text-red-600 hover:bg-red-50 text-sm"
                  >×</button>
                )}
              </div>
            ))}
            <button
              data-testid="payment-add-row"
              onClick={addRow}
              className="text-[12px] uppercase tracking-[0.15em] text-[#71717A] hover:text-[#000] py-1"
            >+ Ödeme Yöntemi Ekle</button>
          </div>

          {/* live totals */}
          <div className="flex items-center justify-between text-sm border-t border-[#E7E5E4] pt-3">
            <span className="text-[#71717A] uppercase tracking-[0.15em] text-[11px]">Toplam</span>
            <span className={"font-finance " + (balanced ? "text-emerald-700" : "text-red-600")}>
              {TRY.format(total)}
              {!balanced && (
                <span className="text-[11px] ml-2">
                  ({total > grand ? "Fazla" : "Eksik"} {TRY.format(Math.abs(total - grand))})
                </span>
              )}
            </span>
          </div>

          {/* reason */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1">
              Düzeltme Nedeni <span className="text-red-600">*</span>
            </label>
            <textarea
              data-testid="payment-correction-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Ör: Müşteri kısmı POS ile ödemişti, kayıt hatası"
              className="w-full px-3 py-2 border border-[#E7E5E4] text-sm"
            />
            <div className="text-[10px] text-[#A8A29E] mt-1">
              En az 5 karakter, en fazla 500 · Denetim kaydına yazılır
            </div>
          </div>

          {history.length > 0 && (
            <details className="border border-[#E7E5E4] px-3 py-2">
              <summary className="cursor-pointer text-[12px] uppercase tracking-[0.15em] text-[#71717A]">
                Bu satışın düzeltme geçmişi ({history.length})
              </summary>
              <div className="mt-2 space-y-2 text-[12px]">
                {history.map((h) => (
                  <div key={h.id} className="border-t border-[#F5F5F4] pt-2">
                    <div className="flex justify-between">
                      <span className="text-[#71717A]">{new Date(h.created_at).toLocaleString("tr-TR")}</span>
                      <span className={h.status === "applied" ? "text-emerald-700" : "text-red-600"}>{h.status}</span>
                    </div>
                    <div className="text-[11px]">
                      <span className="text-[#71717A]">by {h.actor_username}:</span>{" "}
                      {(h.payments_before || []).map((p) => `${PAY_LABEL[p.payment_type]} ${TRY.format(p.amount)}`).join(" + ") || "—"}
                      {" → "}
                      {(h.payments_after || []).map((p) => `${PAY_LABEL[p.payment_type]} ${TRY.format(p.amount)}`).join(" + ") || "—"}
                    </div>
                    <div className="text-[11px] text-[#52525B] italic">&ldquo;{h.reason}&rdquo;</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <div className="border-t border-[#E7E5E4] px-5 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-[#E7E5E4] text-sm hover:bg-[#FAFAF9]"
          >İptal</button>
          <button
            data-testid="payment-correction-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="px-4 py-2 bg-[#000] text-white text-sm hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
          >{submitting ? "Uygulanıyor..." : "Düzeltmeyi Uygula"}</button>
        </div>
      </div>
    </div>
  );
}



/**
 * Phase 54.10 — Metadata (Date / Branch) Correction Dialog.
 *
 * Mirrors PaymentCorrectionDialog's layout so users see a consistent
 * "correction" experience. Only enabled server-side for `admin` and
 * `cemil`; the parent SaleDetail also gates the trigger button.
 *
 * Editable fields:
 *   • Sale date/time    (datetime-local, converted to ISO on submit)
 *   • Sale branch       (branch dropdown fetched at mount)
 * At least one must actually change vs. the current sale values; the
 * mandatory `reason` is length-checked before Submit is enabled.
 */
function MetadataCorrectionDialog({ sale, onClose, onDone }) {
  const currentIso = sale.created_at || null;
  // Convert ISO → `YYYY-MM-DDTHH:mm` (datetime-local format).
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [dateInput, setDateInput] = useState(toLocalInput(currentIso));
  const [branchId, setBranchId] = useState(sale.branch_id || "");
  const [reason, setReason] = useState("");
  const [branches, setBranches] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await usmApi.get("/branches");
        if (mounted) setBranches(r.data || []);
      } catch (_e) { /* fallback: keep current branch as only visible option */ }
    })();
    return () => { mounted = false; };
  }, []);

  const isoFromInput = useMemo(() => {
    if (!dateInput) return null;
    // datetime-local values are in the user's local timezone; new Date
    // parses that directly and toISOString() yields UTC — the backend
    // normalises to UTC anyway.
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  }, [dateInput]);

  const dateChanged = isoFromInput && currentIso && (
    new Date(isoFromInput).getTime() !== new Date(currentIso).getTime()
  );
  const branchChanged = branchId && branchId !== sale.branch_id;
  const anyChange = dateChanged || branchChanged;
  const reasonOk = reason.trim().length >= 5;
  const canSubmit = anyChange && reasonOk && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const body = { reason: reason.trim() };
    if (dateChanged)   body.created_at = isoFromInput;
    if (branchChanged) body.branch_id = branchId;
    try {
      await usmApi.post(`/sales/${sale.id}/correct-metadata`, body);
      toast.success("Satış meta verisi düzeltildi");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Düzeltme uygulanamadı");
    } finally { setSubmitting(false); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || id;

  return (
    <div
      data-testid="metadata-correction-dialog"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-xl border border-[#E7E5E4] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h3 className="font-heading text-lg">Tarih / Şube Düzelt</h3>
          <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-black">Kapat</button>
        </div>

        <div className="p-5 space-y-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
            Satış {sale.sale_no}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.2em] text-neutral-600 mb-1">
                Satış Tarihi / Saati
              </label>
              <input
                data-testid="metadata-date-input"
                type="datetime-local"
                value={dateInput}
                onChange={(e) => setDateInput(e.target.value)}
                className="w-full px-3 py-2 border border-[#E7E5E4] text-sm"
              />
              {dateChanged && (
                <div className="mt-1 text-[10.5px] text-amber-700">
                  {new Date(currentIso).toLocaleString("tr-TR")} → {new Date(isoFromInput).toLocaleString("tr-TR")}
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.2em] text-neutral-600 mb-1">
                Şube
              </label>
              <select
                data-testid="metadata-branch-input"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="w-full px-3 py-2 border border-[#E7E5E4] text-sm bg-white"
              >
                {branches.length === 0 && (
                  <option value={sale.branch_id}>{branchName(sale.branch_id)}</option>
                )}
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {branchChanged && (
                <div className="mt-1 text-[10.5px] text-amber-700">
                  {branchName(sale.branch_id)} → {branchName(branchId)}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.2em] text-neutral-600 mb-1">
              Neden <span className="text-red-500">*</span>
            </label>
            <textarea
              data-testid="metadata-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Bu düzeltmenin nedenini açıklayın (denetim kaydına yazılacaktır)"
              className="w-full px-3 py-2 border border-[#E7E5E4] text-sm"
            />
            <div className="mt-1 text-[10.5px] text-neutral-500">
              En az 5 karakter — denetim kaydına yazılır.
            </div>
          </div>

          <div className="border border-amber-200 bg-amber-50 text-amber-900 p-3 text-[11px] leading-relaxed">
            Bu düzeltme yalnızca satış meta verilerini değiştirir. Envanter,
            stok hareketleri, barkod geçmişi ve ürün sahipliği asla etkilenmez.
            İlgili muhasebe kayıtları (yalnızca tarih ve şube alanları) otomatik
            olarak güncellenir; raporlar/kasa kapanışları yeni değerlere göre
            yeniden hesaplanır. Herhangi bir aşama başarısız olursa TÜM
            değişiklik geri alınır.
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#E7E5E4] bg-[#FAFAF9] flex items-center justify-end gap-3">
          <button
            data-testid="metadata-correction-cancel"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[#E7E5E4] hover:bg-white"
          >Vazgeç</button>
          <button
            data-testid="metadata-correction-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="px-4 py-2 bg-[#000] text-white text-sm hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
          >{submitting ? "Uygulanıyor..." : "Düzeltmeyi Uygula"}</button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * Phase 54.11 — WhatsApp Share Dialog
 *
 * WhatsApp's `wa.me/<phone>` scheme does NOT support attaching files
 * (Meta restriction — only the paid Business Cloud API can send media
 * programmatically). The standard pattern used by every jewelry /
 * boutique store is:
 *
 *   1. Download the receipt PDF locally so the operator has the file
 *      on their device / desktop.
 *   2. Open wa.me with a prefilled text message ("Merhaba, satış
 *      fişiniz ektedir…").
 *   3. Operator manually attaches the just-downloaded PDF from their
 *      files — one tap on mobile, one click on desktop.
 *
 * Phone number is prefilled from `sale.customer_phone` if present.
 * The dialog normalises it to E.164 (strips everything but digits,
 * adds +90 for local Turkish numbers) before wa.me can consume it.
 * ═══════════════════════════════════════════════════════════════════════ */

function normalisePhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  // Turkish local numbers (10 digits starting with 5xx) → prefix 90.
  if (digits.length === 10 && digits.startsWith("5")) return "90" + digits;
  // Turkish with leading 0 (11 digits: 05xx…) → strip 0, prefix 90.
  if (digits.length === 11 && digits.startsWith("0")) return "90" + digits.slice(1);
  // Already international (12+ digits): return as-is.
  return digits;
}

function WhatsAppShareDialog({ sale, initialPhone, filename, onClose, generatePdfBlob, onAudited }) {
  const [phone, setPhone] = useState(initialPhone || "");
  const [message, setMessage] = useState(
    `Merhaba,\nSatın almış olduğunuz ürünün satış fişi ektedir.\nBizi tercih ettiğiniz için teşekkür ederiz.\nUzungöl Kuyumculuk`
  );
  const [running, setRunning] = useState(false);

  const normalised = useMemo(() => normalisePhone(phone), [phone]);
  const valid = normalised.length >= 10;

  const send = async () => {
    if (!valid) {
      toast.error("Geçerli bir telefon numarası girin");
      return;
    }
    setRunning(true);
    try {
      // Step 1 — generate and download the PDF so the operator has it.
      const blob = await generatePdfBlob();
      const dlName = filename || `${sale.sale_no}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = dlName;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      window.setTimeout(() => {
        try { a.remove(); } catch (_) { /* detached */ }
        try { URL.revokeObjectURL(url); } catch (_) { /* released */ }
      }, 500);

      // Step 2 — open WhatsApp with the prefilled message. On mobile
      // `wa.me` deep-links straight to the app; on desktop it opens
      // web.whatsapp.com in a new tab. `noopener` so we don't hand
      // window.opener to an external site.
      const waUrl = `https://wa.me/${normalised}?text=${encodeURIComponent(message)}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");

      // Step 3 — audit.
      onAudited({ phone: normalised, filename: dlName });

      toast.success(
        "PDF indirildi. WhatsApp'ta sohbet açıldı — PDF'yi manuel olarak ekleyin."
      );
      onClose();
    } catch (e) {
      console.error("[whatsapp-share] failed:", e);
      toast.error("Paylaşım başarısız: " + (e?.message || e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="whatsapp-share-dialog"
        className="bg-white w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-emerald-600" />
            <h2 className="font-heading text-lg">WhatsApp ile Gönder</h2>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            className="text-[#71717A] hover:text-[#0A0A0A] disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 text-[11px] px-3 py-2 leading-snug">
            <b>Nasıl çalışır:</b> PDF cihazınıza indirilecek ve WhatsApp
            sohbeti otomatik açılacak. WhatsApp&apos;ta{" "}
            <span className="whitespace-nowrap">📎 → Belge</span> ile indirilen
            PDF&apos;yi ekleyip gönderin.
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              Müşteri Telefonu
            </span>
            <input
              data-testid="wa-phone-input"
              autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+90 5xx xxx xx xx"
              className="mt-1 w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-emerald-500 outline-none font-mono text-sm"
            />
            {phone && !valid && (
              <span className="text-[10px] text-red-600 mt-1 inline-block">
                Numara en az 10 haneli olmalı
              </span>
            )}
            {phone && valid && (
              <span className="text-[10px] text-[#71717A] mt-1 inline-block font-mono">
                → +{normalised}
              </span>
            )}
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              Mesaj
            </span>
            <textarea
              data-testid="wa-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="mt-1 w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-emerald-500 outline-none text-[13px] font-sans resize-none"
            />
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={running}
              className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9] disabled:opacity-40"
            >
              İptal
            </button>
            <button
              data-testid="wa-send-btn"
              onClick={send}
              disabled={!valid || running}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.2em] px-6 py-3"
            >
              {running
                ? <Loader2 size={14} className="animate-spin" />
                : <MessageCircle size={14} />}
              {running ? "Hazırlanıyor…" : "PDF İndir & WhatsApp Aç"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



/* ═══════════════════════════════════════════════════════════════════════
 * Phase 54.12 — Warranty Section (Receipt inline)
 *
 * Rendered inside the print-friendly Receipt. Shows the warranty period,
 * start date (= sale date), and computed expiry — all bilingual. For
 * Lifetime the expiry cell reads "Süresiz / Unlimited", for No Warranty
 * it collapses to a single note so the section stays elegant.
 * ═══════════════════════════════════════════════════════════════════════ */
function WarrantySection({ sale }) {
  const months = sale.warranty_months == null ? 12 : sale.warranty_months;
  const startDate = new Date(sale.created_at);
  const expiry = computeExpiry(sale.created_at, months);
  const label = warrantyLabel(months);
  const isLifetime = months === -1;
  const isNone = months === 0;

  return (
    <div
      data-testid="receipt-warranty-section"
      className="mb-5 border-t border-dashed border-[#E7E5E4] pt-3"
    >
      <div className="grid grid-cols-3 gap-3 text-[12px]">
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#71717A]">
            Garanti Süresi <span className="text-[#A8A29E]">/ Warranty Period</span>
          </div>
          <div data-testid="warranty-period" className="mt-0.5 font-medium">{label}</div>
        </div>
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#71717A]">
            Garanti Başlangıcı <span className="text-[#A8A29E]">/ Warranty Start</span>
          </div>
          <div data-testid="warranty-start" className="mt-0.5 font-mono">
            {isNone ? "—" : fmtWarrantyDate(startDate)}
          </div>
        </div>
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#71717A]">
            Garanti Bitişi <span className="text-[#A8A29E]">/ Warranty Expiry</span>
          </div>
          <div data-testid="warranty-expiry" className="mt-0.5 font-mono">
            {isLifetime
              ? "Süresiz / Unlimited"
              : isNone
                ? "—"
                : fmtWarrantyDate(expiry)}
          </div>
        </div>
      </div>
      {isNone && (
        <div className="mt-2 text-[10px] italic text-[#A8A29E]">
          Bu ürün garanti kapsamı dışındadır. / This product is not covered by warranty.
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * Phase 54.12 — Warranty Correction Dialog
 *
 * Admin/cemil-only. Changes ONLY the warranty period. Same visual
 * language + audit contract as the Payment and Metadata correction
 * dialogs. Backend enforces the allowed values.
 * ═══════════════════════════════════════════════════════════════════════ */
function WarrantyCorrectionDialog({ sale, onClose, onDone }) {
  const currentMonths = sale.warranty_months == null ? 12 : sale.warranty_months;
  const [months, setMonths] = useState(currentMonths);
  const [reason, setReason] = useState("");
  const [running, setRunning] = useState(false);

  const changed = months !== currentMonths;
  const valid = changed && reason.trim().length >= 5;
  const previewExpiry = computeExpiry(sale.created_at, months);

  const submit = async () => {
    if (!valid) return;
    setRunning(true);
    try {
      await usmApi.post(`/sales/${sale.id}/correct-warranty`, {
        warranty_months: months,
        reason: reason.trim(),
      });
      toast.success("Garanti süresi güncellendi");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncelleme başarısız");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="warranty-correction-dialog"
        className="bg-white w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-amber-600" />
            <h2 className="font-heading text-lg">Garantiyi Düzenle</h2>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            className="text-[#71717A] hover:text-[#0A0A0A] disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="bg-amber-50 border border-amber-200 text-amber-900 text-[11px] px-3 py-2 leading-snug">
            Bu düzeltme <b>yalnızca garanti bilgisini</b> değiştirir. Ödeme,
            muhasebe, envanter veya rapor etkilenmez.
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              Garanti Süresi
            </span>
            <select
              data-testid="warranty-months-select"
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value, 10))}
              className="mt-1 w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-amber-500 outline-none text-sm bg-white"
            >
              {WARRANTY_OPTIONS.map((o) => (
                <option key={o.months} value={o.months}>
                  {o.label_tr} / {o.label_en}
                  {o.isDefault ? " (Varsayılan)" : ""}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-[#71717A] mt-1 inline-block font-mono">
              Bitiş: {months === -1
                ? "Süresiz"
                : months === 0
                  ? "—"
                  : fmtWarrantyDate(previewExpiry)}
            </span>
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              Değişiklik Nedeni <span className="text-red-600">*</span>
            </span>
            <textarea
              data-testid="warranty-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Denetim kaydına yazılacak"
              className="mt-1 w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-amber-500 outline-none text-[13px] resize-none"
            />
            <span className="text-[10px] text-[#A8A29E]">En az 5 karakter</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={running}
              className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9] disabled:opacity-40"
            >
              İptal
            </button>
            <button
              data-testid="warranty-save-btn"
              onClick={submit}
              disabled={!valid || running}
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.2em] px-6 py-3"
            >
              {running
                ? <Loader2 size={14} className="animate-spin" />
                : <ShieldCheck size={14} />}
              {running ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * Phase 54.12 — Email Share Dialog
 *
 * The operator types the recipient email; everything else (subject,
 * body, PDF filename, attachment) is prefilled or generated from the
 * same client-side pipeline used by the PDF download and WhatsApp
 * share, so all three channels attach the identical file.
 *
 * The PDF blob is base64-encoded and posted to `share-email`, which
 * forwards to Resend and writes both success and failure audit rows.
 * ═══════════════════════════════════════════════════════════════════════ */
const DEFAULT_EMAIL_SUBJECT = "Uzungöl Kuyumculuk - Satış Fişi / Warranty Certificate";
const DEFAULT_EMAIL_BODY =
  "Merhaba,\n" +
  "Satın almış olduğunuz ürünün satış fişi ektedir.\n" +
  "Bu belge aynı zamanda garanti belgesi olarak da geçerlidir.\n" +
  "Bizi tercih ettiğiniz için teşekkür ederiz.\n" +
  "Uzungöl Kuyumculuk";

// Convert a Blob → plain base64 string (no data-URL prefix).
async function blobToBase64(blob) {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  // Chunked conversion so a 500 KB PDF doesn't blow the JS call stack.
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function EmailShareDialog({ sale, initialEmail, filename, onClose, generatePdfBlob }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [subject, setSubject] = useState(DEFAULT_EMAIL_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_EMAIL_BODY);
  const [running, setRunning] = useState(false);

  // Same minimal email validation as the backend's Pydantic EmailStr —
  // just enough to prevent an obviously bad submit. Server is authoritative.
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const send = async () => {
    if (!valid) {
      toast.error("Geçerli bir e-posta adresi girin");
      return;
    }
    setRunning(true);
    try {
      // Reuse the same client-side PDF pipeline — byte-identical to
      // Print / WhatsApp / Download.
      const blob = await generatePdfBlob();
      const dlName = filename || `${sale.sale_no}.pdf`;
      const pdf_base64 = await blobToBase64(blob);

      const r = await usmApi.post(`/sales/${sale.id}/share-email`, {
        recipient_email: email.trim(),
        subject: subject.trim() || DEFAULT_EMAIL_SUBJECT,
        message: message,
        filename: dlName,
        pdf_base64,
      });
      const emailId = r?.data?.email_id;
      toast.success(
        `E-posta gönderildi · ${email.trim()}${emailId ? ` · ${String(emailId).slice(0, 8)}…` : ""}`
      );
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Bilinmeyen hata";
      console.error("[email-share] failed:", e);
      toast.error("E-posta gönderilemedi: " + detail);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="email-share-dialog"
        className="bg-white w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-[#C8A96A]" />
            <h2 className="font-heading text-lg">E-posta ile Gönder</h2>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            className="text-[#71717A] hover:text-[#0A0A0A] disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="bg-[#FAFAF9] border border-[#E7E5E4] text-[#52525B] text-[11px] px-3 py-2 leading-snug">
            Gönderen: <span className="font-mono">info@uzungolkuyumculuk.com</span>
            <br />
            PDF otomatik olarak eklenir · <span className="font-mono">{filename}</span>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              Alıcı E-posta <span className="text-red-600">*</span>
            </span>
            <input
              data-testid="email-recipient-input"
              autoFocus
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="musteri@ornek.com"
              className="mt-1 w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none font-mono text-sm"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              Konu
            </span>
            <input
              data-testid="email-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-[13px]"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
              Mesaj
            </span>
            <textarea
              data-testid="email-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="mt-1 w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-[13px] resize-none"
            />
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={running}
              className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9] disabled:opacity-40"
            >
              İptal
            </button>
            <button
              data-testid="email-send-btn"
              onClick={send}
              disabled={!valid || running}
              className="inline-flex items-center gap-2 bg-[#C8A96A] hover:bg-[#B08D4F] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.2em] px-6 py-3"
            >
              {running
                ? <Loader2 size={14} className="animate-spin" />
                : <Mail size={14} />}
              {running ? "Gönderiliyor…" : "Gönder"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

